import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox, RadioGroup } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Form } from "@progress/kendo-react-form";
import { getReportTemplates, updateBillSettings } from "api";
import { toast } from "react-toastify";
import SubmitButton from "components/common/SubmitButton";
import { Tile } from "components/UIComponents/Tile";
import AddressInputComponent from "components/common/AddressInputComponent";

const BILL_TEMPLATE_TYPE = "BillingTemplate";
const GUID_EMPTY = "00000000-0000-0000-0000-000000000000";

const ADDRESS_TYPES = [
  { label: "Correspondence", value: "Correspondence" },
  { label: "Statement", value: "Statement" },
  { label: "Billing", value: "Billing" },
  { label: "Other", value: "Other" },
];

const BILL_ADDRESS_TYPE = "billAddress";

const BillSettingsDialog = ({
  open,
  onClose,
  matterId,
  matterDefaults,
  onSaved,
}) => {
  const [billTemplates, setBillTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressType, setAddressType] = useState("Billing");
  const [reportLayout, setReportLayout] = useState(null);
  const [addressAsDefault, setAddressAsDefault] = useState(false);
  const [reportLayoutAsDefault, setReportLayoutAsDefault] = useState(false);
  const formSubmitRef = useRef(null);

  const addressInitialValues = {
    [`${BILL_ADDRESS_TYPE}line1`]: "",
    [`${BILL_ADDRESS_TYPE}town`]: "",
    [`${BILL_ADDRESS_TYPE}state`]: "",
    [`${BILL_ADDRESS_TYPE}postcode`]: "",
    [`${BILL_ADDRESS_TYPE}country`]: "",
  };

  useEffect(() => {
    if (open) {
      loadActiveBillTemplates();
    }
  }, [open]);

  useEffect(() => {
    if (!open || !billTemplates?.length) return;
    const defaultId = matterDefaults?.defaultBillResourceId;
    const matterBillId = matterDefaults?.matterBillResourceId;
    const validId = defaultId && defaultId !== GUID_EMPTY ? defaultId : null;
    if (validId) {
      const template = billTemplates.find((t) => t.reportResourceId === validId);
      setReportLayout(template || null);
    } else {
      setReportLayout(null);
    }
    setReportLayoutAsDefault(
      validId != null &&
        matterBillId != null &&
        matterBillId !== GUID_EMPTY &&
        defaultId === matterBillId,
    );
  }, [
    open,
    matterDefaults?.defaultBillResourceId,
    matterDefaults?.matterBillResourceId,
    billTemplates,
  ]);

  const loadActiveBillTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await getReportTemplates(BILL_TEMPLATE_TYPE);
      const all = response?.data?.data || [];
      const active = all.filter((t) => t.active === true);
      setBillTemplates(active);
    } catch (err) {
      toast.error("Failed to load bill templates");
      setBillTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleSave = async (formData) => {
    if (!matterId) {
      toast.error("No matter selected");
      return;
    }
    try {
      setIsSubmitting(true);
      const value = reportLayout?.reportResourceId ?? GUID_EMPTY;

      if (reportLayoutAsDefault) {
        await updateBillSettings(matterId, { defaultBillResourceId: value });
        onSaved?.({ defaultBillResourceId: value });
      } else {
        onSaved?.({ matterBillResourceId: value });
      }

      if (addressAsDefault && formData) {
        // will be wired when API is available
      }
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save bill settings");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog title="Bill Settings" onClose={onClose} className="dialog-sm">
      <Form
        ignoreModified
        initialValues={addressInitialValues}
        onSubmit={handleSave}
        render={(formRenderProps) => {
          formSubmitRef.current = formRenderProps.onSubmit;
          return (
            <div className="p-3 px-4">
              <Tile heading="Bill Address" className="px-4 pb-4 mb-3 overflow-auto">
                <RadioGroup
                  data={ADDRESS_TYPES}
                  value={addressType}
                  onChange={(e) => setAddressType(e.value)}
                  layout="horizontal"
                  className="d-flex flex-wrap radio gap-2 my-2"
                />
                <AddressInputComponent
                  name="Address"
                  type={BILL_ADDRESS_TYPE}
                  width="275px"
                  style={{
                    width: "610px",
                  }}
                />
                <div className="fw-semibold mt-3">
                  <Checkbox
                    label="Set as Default"
                    checked={addressAsDefault}
                    onChange={(e) => setAddressAsDefault(e.value)}
                  />
                </div>
              </Tile>

              <Tile className="px-4 py-4 overflow-auto">
                <Label className="label mb-2 w-100 fw-semibold">Report Layout</Label>
                <ComboBox
                  data={billTemplates}
                  value={reportLayout}
                  onChange={(e) => setReportLayout(e.value)}
                  textField="templateName"
                  dataItemKey="reportResourceId"
                  placeholder={loadingTemplates ? "Loading..." : "Select Type"}
                  disabled={loadingTemplates}
                  style={{ width: "100%", height: "36px" }}
                  suggest={true}
                  filterable={true}
                />
                <div
                  className="fw-semibold mt-3"
                  title={
                    reportLayout?.reportResourceId ===
                    matterDefaults?.defaultBillResourceId
                      ? "This report is already the matter default"
                      : undefined
                  }>
                  <Checkbox
                    label="Set as Matter Default"
                    checked={reportLayoutAsDefault}
                    onChange={(e) => setReportLayoutAsDefault(e.value)}
                    disabled={
                      !!(
                        reportLayout?.reportResourceId &&
                        matterDefaults?.defaultBillResourceId &&
                        matterDefaults.defaultBillResourceId !== GUID_EMPTY &&
                        reportLayout.reportResourceId ===
                          matterDefaults.defaultBillResourceId
                      )
                    }
                  />
                </div>
              </Tile>
            </div>
          );
        }}
      />
      <DialogActionsBar>
        <div className="dialog-footer w-100 d-flex justify-content-end gap-2 px-4">
          <Button className="common-btn bgBtnSecondary" onClick={onClose}>
            Cancel
          </Button>
          <SubmitButton
            onClick={() => formSubmitRef.current?.()}
            isLoading={isSubmitting}
            disabled={isSubmitting}>
            Save
          </SubmitButton>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
};

export default BillSettingsDialog;
